import { useState } from "react";
import { Box, Divider, List } from "@mui/material";
import { Alert, Typography } from "@platform/design-system";

import { CarrierListItem } from "../components";
import { useCarriers } from "../hooks";

import { CarrierListFilters } from "./CarrierListFilters";

import type { Carrier } from "../types";

interface CarrierListPanelProps {
  selectedCarrier: Carrier | null;
  onSelectCarrier: (carrier: Carrier) => void;
}

interface CarrierListProps extends CarrierListPanelProps {
  search: string;
}

const matchesSearch = (carrier: Carrier, search: string): boolean =>
  carrier.name.toLowerCase().includes(search.trim().toLowerCase());

const CarrierList = ({
  search,
  selectedCarrier,
  onSelectCarrier,
}: CarrierListProps): React.JSX.Element => {
  const { carriers, loading, error } = useCarriers();

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (loading && carriers.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body-medium" color="textSecondary">
          Loading carriers...
        </Typography>
      </Box>
    );
  }

  return (
    <List>
      {carriers
        .filter((carrier) => matchesSearch(carrier, search))
        .map((carrier) => (
          <CarrierListItem
            key={carrier.id}
            carrier={carrier}
            selected={carrier.id === selectedCarrier?.id}
            onSelect={onSelectCarrier}
          />
        ))}
    </List>
  );
};

export const CarrierListPanel = ({
  selectedCarrier,
  onSelectCarrier,
}: CarrierListPanelProps): React.JSX.Element => {
  const [search, setSearch] = useState("");

  return (
    <Box
      data-testid="carrier-list-panel"
      height="100%"
      display="flex"
      flexDirection="column"
      border={1}
      borderColor="divider"
      borderRadius={1}
      bgcolor="background.paper"
    >
      <CarrierListFilters search={search} onSearchChange={setSearch} />
      <Divider />
      <Box flex={1} overflow="auto">
        <CarrierList
          search={search}
          selectedCarrier={selectedCarrier}
          onSelectCarrier={onSelectCarrier}
        />
      </Box>
    </Box>
  );
};
